/* eslint-disable react-hooks/exhaustive-deps */
import { useAdminUser, useAlert, useCompany } from '@orientaction/hooks';
import { adminService } from '@orientaction/services';
import { getFromLS, valueAliasRole } from '@orientaction/utils';
import { validate } from 'email-validator';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { inCardService, oaServices } from '../../../components/Form/CreateEditAdmin/constant';
import { apiServiceNative } from '../../../utils/fetchNative';
import {
  BENEFICIARY_ROLE_ID,
  CONSULTANT_ROLE_ID,
  EMAIL_REGEX,
} from '../shared/constants';
import {
  buildUpHierarchyPayload,
  mapCategoryTests,
  mapManagerOptions,
  mapRoleOptions,
  needsAttachmentManager,
  readRole,
  unwrapCollection,
} from '../shared/utils';
import { CategoryTestOption } from '../shared/types';
import { CREATE_SUCCESS, getCreateAccountTitle } from './constants';
import { ExistingUser, InfosForm, ManagerOption, WizardStep } from './types';

const qs = require('qs');

const consultantServicesCatalog = oaServices.concat(inCardService);

const mapExistingUser = (user: any): ExistingUser => ({
  id: user.id,
  email: user.email,
  firstname: user.firstname,
  lastname: user.lastname,
  photoUri: user.photoUri,
  company_user_role: readRole(user.company_user_role) || undefined,
});

export const useCreateConsultantWizard = () => {
  const history = useHistory();
  const location = useLocation();
  const alert = useAlert();
  const { company, updateArrayAfterUpdateCreate, setCountTabs, countTabs, setTabs } = useAdminUser();
  const { companyUserRole, getCompanyUserRole, getUpHierachy, companies } = useCompany();

  const token = getFromLS('userToken') || '';
  const userConnected = JSON.parse(getFromLS('userInfos') || '{}');
  const companyId = company || userConnected?.company?.id || 1;
  const isSuperAdmin = userConnected?.role?.type === 'admin';
  const connectedRoleId = Number(userConnected?.company_user_role?.id) || 0;

  const queryRole = Number(new URLSearchParams(location.search).get('role'));
  const defaultRoleId =
    queryRole && queryRole !== BENEFICIARY_ROLE_ID ? queryRole : CONSULTANT_ROLE_ID;

  const [step, setStep] = useState<WizardStep>(1);
  const [email, setEmail] = useState('');
  const [existingUser, setExistingUser] = useState<ExistingUser | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [infos, setInfos] = useState<InfosForm>({
    roleId: defaultRoleId,
    firstname: '',
    lastname: '',
    managerId: '',
    serviceIds: [],
    categoryTestIds: [],
  });
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [categoryTests, setCategoryTests] = useState<CategoryTestOption[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const alias = companies.find((item: any) => Number(item.id) === Number(companyId));

  const roles = useMemo(
    () =>
      mapRoleOptions(companyUserRole, {
        isSuperAdmin,
        connectedRoleId,
        aliasName: name => valueAliasRole(name, alias?.attributes?.companyUserRoleAlias) || name,
      }),
    [companyUserRole, alias, isSuperAdmin, connectedRoleId]
  );

  const services = useMemo(
    () =>
      consultantServicesCatalog.map(service => ({
        id: Number(service.id),
        name: service.attributes.name,
      })),
    []
  );

  const isEmailValid = validate(email.trim()) && EMAIL_REGEX.test(email.trim());
  const needsManager = needsAttachmentManager(infos.roleId);
  const isConsultantRole = Number(infos.roleId) === CONSULTANT_ROLE_ID;
  const selectedRoleName = roles.find(role => Number(role.id) === Number(infos.roleId))?.name;
  const pageTitle = getCreateAccountTitle(infos.roleId, selectedRoleName);
  const canSubmit =
    Boolean(infos.roleId) &&
    Boolean(infos.firstname.trim()) &&
    Boolean(infos.lastname.trim()) &&
    (!needsManager || Boolean(infos.managerId)) &&
    (!isConsultantRole || infos.serviceIds.length > 0);

  const existingRoleLabel = useMemo(() => {
    if (!existingUser) {
      return '';
    }
    const role = readRole(existingUser.company_user_role);
    if (!role) {
      return '';
    }
    const fromList = roles.find(item => Number(item.id) === Number(role.id));
    if (fromList?.name) {
      return fromList.name;
    }
    if (role.id === BENEFICIARY_ROLE_ID) {
      return 'Bénéficiaire';
    }
    return role.name || 'Utilisateur';
  }, [existingUser, roles]);

  useEffect(() => {
    if (!companyUserRole?.length) {
      getCompanyUserRole().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      try {
        const response = await apiServiceNative.get('/api/category-tests');
        if (cancelled) {
          return;
        }
        const mapped = mapCategoryTests(response?.data || response);
        setCategoryTests(mapped);
      } catch {
        if (!cancelled) {
          setCategoryTests([]);
        }
      }
    };
    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!infos.roleId || !needsAttachmentManager(infos.roleId)) {
      setManagers([]);
      return undefined;
    }
    let cancelled = false;
    const loadManagers = async () => {
      try {
        const response = await getUpHierachy(
          buildUpHierarchyPayload({
            companyId,
            roleId: infos.roleId,
            isSuperAdmin,
            userConnected,
          })
        );
        if (cancelled) {
          return;
        }
        setManagers(mapManagerOptions(response));
      } catch {
        if (!cancelled) {
          setManagers([]);
        }
      }
    };
    loadManagers();
    return () => {
      cancelled = true;
    };
  }, [infos.roleId, companyId]);

  const goBack = () => {
    history.push('/admin/users-resultats');
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (existingUser) {
      setExistingUser(null);
    }
  };

  const updateInfos = (patch: Partial<InfosForm>) => {
    setInfos(prev => ({ ...prev, ...patch }));
  };

  const fetchExistingUser = async (value: string) => {
    const params = qs.stringify(
      {
        filters: { email: { $eq: value.trim().toLowerCase() } },
        populate: ['company_user_role'],
        fields: ['email', 'firstname', 'lastname', 'photoUri'],
      },
      { encodeValuesOnly: true }
    );
    const response = await apiServiceNative.get(`/api/users?${params}`);
    const list = unwrapCollection(response);
    return list[0] ? mapExistingUser(list[0]) : null;
  };

  const goNextFromEmail = async () => {
    if (!isEmailValid || existingUser) {
      return;
    }
    setCheckingEmail(true);
    try {
      const found = await fetchExistingUser(email);
      if (found) {
        setExistingUser(found);
        return;
      }
      setExistingUser(null);
      setStep(2);
    } catch {
      alert.openSimpleAlert();
    } finally {
      setCheckingEmail(false);
    }
  };

  const openExistingUserProfile = () => {
    if (!existingUser) {
      return;
    }
    const roleId = readRole(existingUser.company_user_role)?.id;
    if (roleId === BENEFICIARY_ROLE_ID) {
      history.push(`/admin/users-resultats/beneficiaire/${existingUser.id}`);
      return;
    }
    history.push(`/admin/users-resultats/consultant/${existingUser.id}`);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    try {
      const selectedIds = new Set(infos.serviceIds.map(Number));
      const payload = {
        email: email.trim().toLowerCase(),
        username: email.trim().toLowerCase(),
        firstname: infos.firstname.trim(),
        lastname: infos.lastname.trim(),
        company: companyId,
        company_user_role: Number(infos.roleId),
        upper_hierarchy: infos.managerId || null,
        rattachement: true,
        Direction: userConnected.Direction || null,
        services: consultantServicesCatalog.map(service => ({
          ...service,
          isChecked: isConsultantRole && selectedIds.has(Number(service.id)),
        })),
        allowed_services: isConsultantRole
          ? infos.serviceIds.map(id => ({ id: Number(id) }))
          : [],
        category_tests: isConsultantRole
          ? categoryTests
              .filter(category => infos.categoryTestIds.includes(category.id))
              .map(category => ({
                id: category.id,
                name: category.name,
                subtitle: category.subtitle,
                priority: category.priority,
              }))
          : [],
      };
      const response = await adminService.register(payload, token);
      if (response?.data.data === 'E-mail already exist') {
        alert.setOpenAlert(
          true,
          'Cette adresse e-mail existe déjà, merci d’en utiliser une autre',
          'error'
        );
        setStep(1);
        const found = await fetchExistingUser(email);
        if (found) {
          setExistingUser(found);
        }
        return;
      }
      const created = response?.data?.data;
      if (created) {
        updateArrayAfterUpdateCreate(created);
        const roleId = Number(infos.roleId);
        setCountTabs({
          ...countTabs,
          [roleId]: (countTabs[roleId] || 0) + 1,
        });
        setTabs(roleId);
      }
      alert.setOpenAlert(true, CREATE_SUCCESS, 'success');
      history.push('/admin/users-resultats');
    } catch {
      alert.openSimpleAlert();
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    if (step === 1) {
      await goNextFromEmail();
    }
  };

  const goPrevious = () => {
    if (step === 1) {
      goBack();
      return;
    }
    setStep(1);
  };

  return {
    step,
    email,
    existingUser,
    existingRoleLabel,
    checkingEmail,
    infos,
    roles,
    managers,
    services,
    categoryTests,
    isConsultantRole,
    pageTitle,
    isEmailValid,
    canSubmit,
    submitting,
    handleEmailChange,
    updateInfos,
    openExistingUserProfile,
    goBack,
    goNext,
    goPrevious,
    handleSubmit,
  };
};
